import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  boardBook,
  buildBoard,
  buildGameMarkets,
  cardRows,
  contestTeams,
  hotOrder,
  isMainKind,
  matchupTitle,
  MARKET_LABEL,
  siblingBooks,
  type Board,
  type Book,
  type LineOverrides,
  type PriceMode,
} from "../lib/board";
import { isPublicUnavailable } from "../lib/api";
import { cn } from "../lib/cn";
import { fmtAgo } from "../lib/format";
import { useSession } from "../lib/omauth";
import { usePriceMode } from "../lib/price-mode";
import {
  useContest,
  useContestLiquidity,
  useContestMarkets,
  useContestSearch,
  useLeagues,
  usePartners,
  useUpcomingContests,
} from "../lib/queries";
import { useFeedStatus, useLiveContests, type FeedStatus } from "../lib/stream";
import type { Contest, MarketDef, Position } from "../lib/types";
import { useDebounced } from "../lib/useDebounced";
import { useNow } from "../lib/useNow";
import { ConnectButton, friendlySignInError } from "./ConnectButton";
import { BOARD_GRID, GameCard } from "./GameCard";
import { GameDetail } from "./GameDetail";
import { TopBar, type LeagueTab } from "./TopBar";
import { PanelEmpty, VenuePanel } from "./VenuePanel";

/** Hot looks this far ahead. */
const HOT_WINDOW_MS = 36 * 3_600_000;
/** Each board game costs one liquidity request (no bulk endpoint), so the board is capped. */
const HOT_LIMIT = 30;
const LEAGUE_LIMIT = 40;
const SEARCH_LIMIT = 30;

/** What the venue panel is showing: one book of one market on one game. */
interface Selection {
  contestId: string;
  marketKey: string;
  bookId: string;
}

// Building a board is cheap but not free; the query cache hands back the same array until a
// price moves, so memoise on that identity (per price mode).
const boardCache = new WeakMap<Position[], Partial<Record<PriceMode, Board>>>();
function boardFor(positions: Position[], mode: PriceMode): Board {
  let entry = boardCache.get(positions);
  if (!entry) {
    entry = {};
    boardCache.set(positions, entry);
  }
  return (entry[mode] ??= buildBoard(positions, mode));
}

/** The active tab lives in the URL hash so a league view survives a reload and can be shared. */
function useTab(): [string, (tab: string) => void] {
  const [tab, setTabState] = useState(() => decodeURIComponent(window.location.hash.slice(1)) || "hot");
  const setTab = (next: string) => {
    setTabState(next);
    const url = next === "hot" ? window.location.pathname : `#${encodeURIComponent(next)}`;
    window.history.replaceState(null, "", url);
  };
  return [tab, setTab];
}

/** A game's own page is `/game/<id>`, so it can be linked, shared and reloaded. */
function gameIdFromPath(): string | null {
  const m = window.location.pathname.match(/^\/game\/([^/]+)\/?$/);
  return m ? decodeURIComponent(m[1]) : null;
}

function useGameRoute(): [string | null, (id: string | null) => void] {
  const [id, setId] = useState(gameIdFromPath);
  useEffect(() => {
    const onPop = () => setId(gameIdFromPath());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const go = (next: string | null) => {
    const url = next ? `/game/${encodeURIComponent(next)}` : `/${window.location.hash}`;
    if (url !== window.location.pathname + window.location.hash) window.history.pushState(null, "", url);
    setId(next);
  };
  return [id, go];
}

function leagueTabs(contests: Contest[], names: Map<string, string>): LeagueTab[] {
  const counts = new Map<string, number>();
  for (const c of contests) if (c.league_id) counts.set(c.league_id, (counts.get(c.league_id) ?? 0) + 1);
  return [...counts]
    .map(([id, count]) => ({ id, name: names.get(id) ?? "Other", count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}

function pickGames(contests: Contest[], tab: string): Contest[] {
  if (tab !== "hot") return contests.filter((c) => c.league_id === tab).slice(0, LEAGUE_LIMIT);
  const horizon = Date.now() + HOT_WINDOW_MS;
  const soon = contests.filter((c) => !c.starts_at || Date.parse(c.starts_at) <= horizon);
  return (soon.length ? soon : contests).slice(0, HOT_LIMIT);
}

interface Resolved {
  label: string;
  multi: boolean;
  book: Book | null;
  /** The lines the viewer can flip to from this book. */
  siblings: Book[];
  mainId?: string;
}

/** Find the selected book, and the lines it can flip to, in a game's current positions. */
function resolveSelection(
  sel: Selection,
  positions: Position[],
  mode: PriceMode,
  markets: MarketDef[] | undefined,
): Resolved | null {
  if (isMainKind(sel.marketKey)) {
    const market = boardFor(positions, mode)[sel.marketKey];
    if (!market) return null;
    return {
      label: MARKET_LABEL[sel.marketKey],
      multi: false,
      book: market.books.find((b) => b.id === sel.bookId) ?? null,
      siblings: market.books,
      mainId: market.mainId,
    };
  }
  const all = buildGameMarkets(positions, markets, mode);
  const group = [...all.game, ...all.player].find((g) => g.key === sel.marketKey);
  if (!group) return null;
  const book = group.books.find((b) => b.id === sel.bookId) ?? null;
  return { label: group.label, multi: group.multi, book, siblings: book ? siblingBooks(group, book) : [] };
}

export function OddsScreen({ signInError }: { signInError: string | null }) {
  const session = useSession();
  const signedIn = session !== null;
  const [tab, setTab] = useTab();
  const [selection, setSelection] = useState<Selection | null>(null);
  const [detailId, setDetailId] = useGameRoute();
  const [overrides, setOverrides] = useState<Record<string, LineOverrides>>({});
  const [query, setQuery] = useState("");
  const searchTerm = useDebounced(query.trim(), 300);
  const searching = searchTerm.length >= 2;
  const queryClient = useQueryClient();
  const mode = usePriceMode();
  const mainRef = useRef<HTMLElement | null>(null);

  const contestsQ = useUpcomingContests();
  const searchQ = useContestSearch(searching ? searchTerm : null);
  const leaguesQ = useLeagues();
  const partnersQ = usePartners();
  const marketsQ = useContestMarkets(detailId);
  const feed = useFeedStatus();

  const leagueNames = useMemo(
    () => new Map((leaguesQ.data ?? []).map((l) => [l.id, l.name])),
    [leaguesQ.data],
  );
  const contests = useMemo(() => contestsQ.data ?? [], [contestsQ.data]);
  const tabs = useMemo(() => leagueTabs(contests, leagueNames), [contests, leagueNames]);
  // A stale hash (a league with no games this week) quietly falls back to Hot.
  const activeTab = tab !== "hot" && contestsQ.isSuccess && !tabs.some((t) => t.id === tab) ? "hot" : tab;

  const games = useMemo(
    () => (searching ? (searchQ.data ?? []).slice(0, SEARCH_LIMIT) : pickGames(contests, activeTab)),
    [searching, searchQ.data, contests, activeTab],
  );
  // The open game: from what's loaded when it's there, else fetched by id (a shared link).
  const knownDetail = detailId
    ? (games.find((c) => c.id === detailId) ?? contests.find((c) => c.id === detailId))
    : undefined;
  const detailQ = useContest(detailId, knownDetail);
  const detail = detailQ.data ?? knownDetail ?? null;

  // The open game's liquidity is loaded and streamed even when it isn't on the current board.
  const ids = useMemo(() => {
    const out = games.map((c) => c.id);
    if (detail && !out.includes(detail.id)) out.push(detail.id);
    return out;
  }, [games, detail]);
  const liquidity = useContestLiquidity(ids);
  useLiveContests(ids);
  const byId = new Map(ids.map((id, i) => [id, liquidity[i]] as const));

  const cards = games.map((contest) => {
    const q = byId.get(contest.id);
    return {
      contest,
      pending: q?.isPending ?? true,
      board: q?.data ? boardFor(q.data, mode) : null,
      updatedAt: q?.dataUpdatedAt ?? 0,
    };
  });
  // Games nobody is quoting drop off; games still loading hold their place as skeletons.
  const shown = cards.filter((c) => c.pending || (c.board && c.board.venueCount > 0));
  const allSettled = cards.length > 0 && cards.every((c) => !c.pending);

  // Hot order. Ranked on LISTED prices whatever the fee switch says — a display choice must never
  // reshuffle the list — and frozen once every game has loaded, so live price moves don't make
  // rows jump under the viewer's cursor. It re-ranks only when the set of games changes or on
  // Refresh (which clears `frozenOrder`).
  const hot = !searching && activeTab === "hot";
  const idsKey = games.map((c) => c.id).join(",");
  const frozenOrder = useRef<{ key: string; order: string[] } | null>(null);
  if (hot) {
    const frozen = frozenOrder.current?.key === idsKey ? frozenOrder.current.order : null;
    if (frozen) {
      const rank = new Map(frozen.map((id, i) => [id, i]));
      shown.sort((a, b) => (rank.get(a.contest.id) ?? 1e9) - (rank.get(b.contest.id) ?? 1e9));
    } else {
      const listed = (c: (typeof cards)[number]) => {
        const data = byId.get(c.contest.id)?.data;
        return data ? boardFor(data, "listed") : null;
      };
      shown.sort((a, b) => {
        const la = listed(a);
        const lb = listed(b);
        return la && lb ? hotOrder(la, lb) : la ? -1 : lb ? 1 : 0;
      });
      if (allSettled) frozenOrder.current = { key: idsKey, order: shown.map((c) => c.contest.id) };
    }
  }
  const updatedAt = cards.reduce((max, c) => Math.max(max, c.updatedAt), 0);

  const leagueOf = (c: Contest) => (c.league_id ? leagueNames.get(c.league_id) : undefined);
  const findContest = (id: string) =>
    games.find((c) => c.id === id) ?? (detail?.id === id ? detail : undefined) ?? contests.find((c) => c.id === id);

  const toggle = (next: Selection) =>
    setSelection((cur) =>
      cur && cur.contestId === next.contestId && cur.marketKey === next.marketKey && cur.bookId === next.bookId
        ? null
        : next,
    );

  useEffect(() => {
    if (!selection && !detail) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (selection) setSelection(null);
      else setDetailId(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selection, detail]);

  const openGame = (contest: Contest) => {
    setDetailId(contest.id);
    setSelection(null);
    mainRef.current?.scrollTo({ top: 0 });
  };
  const changeTab = (next: string) => {
    setTab(next);
    setQuery("");
    setSelection(null);
    setDetailId(null);
  };
  const changeQuery = (next: string) => {
    setQuery(next);
    setSelection(null);
    setDetailId(null);
  };

  let panel: React.ReactNode = null;
  if (selection) {
    const sel = selection;
    const contest = findContest(sel.contestId);
    const positions = byId.get(sel.contestId)?.data;
    const resolved = contest && positions ? resolveSelection(sel, positions, mode, marketsQ.data) : null;
    if (contest && positions && resolved) {
      const title = matchupTitle(cardRows(boardFor(positions, mode), contestTeams(contest)), contest.title);
      const pick = (bookId: string) => {
        setSelection({ ...sel, bookId });
        // Flipping a board market's line also moves that game's card to the chosen line.
        if (isMainKind(sel.marketKey)) {
          const kind = sel.marketKey;
          setOverrides((o) => ({ ...o, [contest.id]: { ...o[contest.id], [kind]: bookId } }));
        }
      };
      panel = (
        <VenuePanel
          contest={contest}
          title={title}
          marketLabel={resolved.label}
          subject={resolved.multi ? resolved.book?.participantName : null}
          league={leagueOf(contest)}
          book={resolved.book}
          lines={
            resolved.book
              ? { books: resolved.siblings, currentId: resolved.book.id, mainId: resolved.mainId, onPick: pick }
              : null
          }
          partners={partnersQ.data}
          onClose={() => setSelection(null)}
        />
      );
    }
  }

  const heading = searching
    ? { title: "Search", sub: `Open games matching “${searchTerm}”` }
    : activeTab === "hot"
      ? { title: "Hot right now", sub: "Games in the next 36 hours, most venues first" }
      : { title: leagueNames.get(activeTab) ?? "League", sub: "Open games this week, soonest first" };
  const listQuery = searching ? searchQ : contestsQ;
  const limit = searching ? SEARCH_LIMIT : activeTab === "hot" ? HOT_LIMIT : LEAGUE_LIMIT;

  // Signed out and the public feed isn't set up here: signing in is the only way to see prices.
  const needsSignIn = !signedIn && isPublicUnavailable(listQuery.error);

  return (
    <div className="flex h-full flex-col">
      <TopBar
        tabs={tabs}
        active={searching ? "" : activeTab}
        onTab={changeTab}
        query={query}
        onQuery={changeQuery}
        signedIn={signedIn}
      />

      <div className="flex min-h-0 flex-1">
        <main ref={mainRef} className="min-w-0 flex-1 overflow-y-auto px-3 pb-16 sm:px-4">
          <div className="mx-auto max-w-[68rem]">
            {detail ? (
              <GameDetail
                contest={detail}
                league={leagueOf(detail)}
                positions={byId.get(detail.id)?.data}
                loading={byId.get(detail.id)?.isPending ?? true}
                markets={marketsQ.data}
                marketsReady={!marketsQ.isPending}
                mode={mode}
                partners={partnersQ.data}
                onBack={() => {
                  setDetailId(null);
                  setSelection(null);
                }}
              />
            ) : detailId ? (
              // Not found by id AND not among the loaded games.
              detailQ.isError && !contestsQ.isPending ? (
                <div className="pt-10">
                  <Notice
                    title="Couldn't find that game"
                    body="It may have finished, or the link is wrong."
                    action={{ label: "See all games", onClick: () => setDetailId(null) }}
                  />
                </div>
              ) : (
                <div className="skeleton mt-16 h-96 rounded-2xl" />
              )
            ) : (
              <>
                <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2 pb-4 pt-5">
                  <div>
                    <h1 className="font-display text-2xl font-extrabold tracking-tight sm:text-[1.7rem]">
                      {heading.title}
                    </h1>
                    <p className="mt-0.5 text-[13px] text-dim">{heading.sub}</p>
                  </div>
                  <FeedLine
                    feed={feed}
                    updatedAt={updatedAt}
                    onRefresh={() => {
                      frozenOrder.current = null; // a deliberate refresh may re-rank Hot
                      void queryClient.invalidateQueries({ queryKey: ["liquidity"] });
                    }}
                  />
                </div>

                {!signedIn && !needsSignIn && <SnapshotBanner error={signInError} />}

                <ColumnHeads />

                {needsSignIn ? (
                  <Notice
                    title="Sign in to see the odds"
                    body="Every venue's price for every game, side by side — free with an OpenMarkets account. BestOpen is read-only: it can't see your balances or place orders."
                  >
                    <ConnectButton label="Sign in with OpenMarkets" size="md" />
                    {signInError && <p className="text-[12px] text-live">{friendlySignInError(signInError)}</p>}
                  </Notice>
                ) : listQuery.isError ? (
                  <Notice
                    title={searching ? "Search didn't work" : "Couldn't load games"}
                    body={listQuery.error instanceof Error ? listQuery.error.message : "Something went wrong."}
                    action={{ label: "Try again", onClick: () => void listQuery.refetch() }}
                  />
                ) : listQuery.isPending ? (
                  <Skeletons count={6} />
                ) : shown.length === 0 && (allSettled || cards.length === 0) ? (
                  searching ? (
                    <Notice title="No games found" body={`No open game matches “${searchTerm}”. Try a team or a city.`} />
                  ) : (
                    <Notice
                      title="Nothing to compare yet"
                      body="No venue is quoting these games right now. Check back closer to game time."
                    />
                  )
                ) : (
                  <div className="flex flex-col gap-2">
                    {shown.map((c, i) => {
                      const board = c.board;
                      if (!board) {
                        return <div key={c.contest.id} className="skeleton h-[6.25rem] rounded-2xl sm:h-[5.5rem]" />;
                      }
                      const id = c.contest.id;
                      return (
                        <GameCard
                          key={id}
                          index={i}
                          contest={c.contest}
                          board={board}
                          league={leagueOf(c.contest)}
                          overrides={overrides[id]}
                          partners={partnersQ.data}
                          selectedKind={
                            selection && selection.contestId === id && isMainKind(selection.marketKey)
                              ? selection.marketKey
                              : null
                          }
                          onSelect={(kind) => {
                            const book = boardBook(board[kind], overrides[id]?.[kind]);
                            if (book) toggle({ contestId: id, marketKey: kind, bookId: book.id });
                          }}
                          onOpen={() => openGame(c.contest)}
                        />
                      );
                    })}
                    {games.length >= limit && (
                      <p className="pt-2 text-center text-[12px] text-faint">Showing the first {games.length} games.</p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </main>

        {/* The full-game view carries its own comparison, so it gets the whole width. */}
        {!detailId && (
          <aside className="hidden w-[24rem] shrink-0 overflow-y-auto border-l border-line bg-panel/80 backdrop-blur lg:block">
            {panel ?? <PanelEmpty />}
          </aside>
        )}
      </div>

      {panel && (
        <div className="fixed inset-0 z-30 lg:hidden" role="dialog" aria-modal="true">
          <button
            type="button"
            aria-label="Close"
            className="fade-in absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setSelection(null)}
          />
          <div
            className="sheet-up absolute inset-x-0 bottom-0 max-h-[82vh] overflow-y-auto rounded-t-3xl border-t border-line bg-panel"
            style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
          >
            <div className="mx-auto mt-2.5 h-1 w-10 rounded-full bg-cell-hi" />
            {panel}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The signed-out CTA, the 4C Odds pattern: the board is a snapshot, and signing in makes it live.
 * Signing in is also the front door to an OpenMarkets account.
 */
function SnapshotBanner({ error }: { error: string | null }) {
  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-accent/30 bg-panel/80 px-4 py-3 backdrop-blur-sm">
      <div className="text-[13px] leading-snug">
        <p>
          <span className="font-bold">You're looking at a snapshot.</span>{" "}
          <span className="text-dim">
            Prices refresh every minute. Sign in with OpenMarkets and they update live, as venues move — it's free.
          </span>
        </p>
        {error && <p className="mt-1 text-[12px] text-live">{friendlySignInError(error)}</p>}
      </div>
      <ConnectButton label="Sign in for live prices" size="md" />
    </div>
  );
}

function ColumnHeads() {
  const label = "text-[10px] font-semibold uppercase tracking-[0.14em] text-faint";
  return (
    <div className={cn(BOARD_GRID, "px-2.5 pb-2 sm:px-3")}>
      <span className="hidden sm:block" />
      <span />
      <span className={cn(label, "text-center sm:text-left")}>
        <span className="sm:hidden">ML</span>
        <span className="hidden sm:inline">Moneyline</span>
      </span>
      <span className={cn(label, "text-center sm:text-left")}>Spread</span>
      <span className={cn(label, "text-center sm:text-left")}>Total</span>
    </div>
  );
}

function FeedLine({ feed, updatedAt, onRefresh }: { feed: FeedStatus; updatedAt: number; onRefresh: () => void }) {
  const now = useNow(5_000);
  if (feed === "live") {
    return (
      <span className="flex items-center gap-2 rounded-full border border-line bg-panel/70 px-3 py-1.5 text-[12px] text-dim">
        <span className="brand-gradient pulse h-2 w-2 rounded-full" />
        Live — prices update as venues move
      </span>
    );
  }
  return (
    <span className="flex items-center gap-2 rounded-full border border-line bg-panel/70 px-3 py-1.5 text-[12px] text-dim">
      <span className="h-2 w-2 rounded-full bg-faint" />
      {feed === "connecting"
        ? "Connecting to live prices…"
        : updatedAt
          ? `Snapshot · updated ${fmtAgo(now - updatedAt)}`
          : "Loading prices…"}
      <button type="button" onClick={onRefresh} className="font-semibold text-accent hover:underline">
        Refresh
      </button>
    </span>
  );
}

function Skeletons({ count }: { count: number }) {
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="skeleton h-[6.25rem] rounded-2xl sm:h-[5.5rem]" style={{ animationDelay: `${i * 90}ms` }} />
      ))}
    </div>
  );
}

function Notice({
  title,
  body,
  action,
  children,
}: {
  title: string;
  body: string;
  action?: { label: string; onClick: () => void };
  children?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-2xl border border-line bg-card/60 px-6 py-14 text-center">
      <div className="font-display text-lg font-bold">{title}</div>
      <p className="max-w-sm text-[13px] text-dim">{body}</p>
      {children && <div className="mt-2 flex flex-col items-center gap-2">{children}</div>}
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          className="brand-gradient mt-2 rounded-full px-4 py-2 text-[13px] font-bold text-accent-ink"
        >
          {action.label}
        </button>
      )}
    </div>
  );
}
